/**
 * FtpConfigPanel — a single FTP/SFTP credential form for ONE of the two
 * independent FTP paths an org can configure:
 *
 *   - configType="download": the org's OWN inbound FTP. When a campaign is
 *     delivered TO this org, its members can push the spots to this FTP from
 *     the Download Manager instead of downloading a ZIP. Gated by the
 *     `ftpEnabled` org flag (which the Download Manager reads).
 *
 *   - configType="delivery": a single EXTERNAL FTP this org auto-pushes every
 *     delivered asset to, on every delivery. No cross-org approval, no rate
 *     limit — handled server-side by the onDeliveryCreatedSendToFtp trigger.
 *     Gated by the `deliveryFtpEnabled` org flag.
 *
 * Each panel owns its own status/form state and calls the shared config
 * callables (saveFtpConfig / checkFtpConfigStatus / testFtpConnection /
 * disableFtpConfig) with its `configType`.
 */

import React, { useContext, useEffect, useState } from 'react';
import { FirebaseContext } from '../Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { useToast } from '../../contexts/ToastContext';
import { Spinner, Button } from 'reactstrap';
import { fetchOrgClients } from '../../services/campaignReport';

interface FtpConfigStatus {
  configured: boolean;
  active?: boolean;
  protocol?: string;
  host?: string;
  port?: number;
  username?: string;
  path?: string;
  defaultFtpPresetId?: string | null;
  clientFilter?: string[];
  updatedAt?: number;
}

interface DownloadPreset {
  id: string;
  name: string;
  prefix?: string;
  suffix?: string;
  format?: string;
  bitrate?: string;
  sampleRate?: string;
  bitDepth?: string;
  channels?: string;
}

interface Props {
  configType: 'download' | 'delivery';
  title: string;
  description: string;
  /** Download config keeps the legacy "Default FTP preset" picker; delivery
   *  sends assets as-is, so it hides it. */
  showPresetField?: boolean;
  /** Delivery config only: show a client allowlist so the org can restrict
   *  which clients' campaigns are pushed to the external FTP. */
  showClientFilter?: boolean;
  /** Notify the parent when this panel's config changes, so a shared audit
   *  log can refresh. */
  onChanged?: () => void;
}

const FtpConfigPanel: React.FC<Props> = ({ configType, title, description, showPresetField, showClientFilter, onChanged }) => {
  const firebase: any = useContext(FirebaseContext);
  const { currentOrgId } = useOrganizationStore();
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<FtpConfigStatus>({ configured: false });
  const [editing, setEditing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    protocol: string;
    host: string;
    port: number;
    username: string;
    password: string;
    path: string;
    defaultFtpPresetId: string;
    clientFilter: string[];
  }>({
    protocol: 'sftp',
    host: '',
    port: 22,
    username: '',
    password: '',
    path: '/',
    defaultFtpPresetId: '',
    clientFilter: [],
  });
  const [presets, setPresets] = useState<DownloadPreset[]>([]);
  const [availableClients, setAvailableClients] = useState<string[]>([]);

  const logPrefix = `[FtpConfigPanel:${configType}]`;

  const loadStatus = async () => {
    if (!firebase || !currentOrgId) return;
    setLoading(true);
    try {
      const res = await firebase.callFunction('checkFtpConfigStatus', { orgId: currentOrgId, configType });
      const data = res.data as FtpConfigStatus;
      console.log(`${logPrefix} config status:`, data);
      setStatus(data);
      if (data.configured) {
        setForm({
          protocol: data.protocol || 'sftp',
          host: data.host || '',
          port: data.port || 22,
          username: data.username || '',
          password: '',
          path: data.path || '/',
          defaultFtpPresetId: data.defaultFtpPresetId || '',
          clientFilter: Array.isArray(data.clientFilter) ? data.clientFilter : [],
        });
      }
    } catch (err: any) {
      console.error(`${logPrefix} failed to load config status:`, err);
      showError('Failed to load FTP status');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebase, currentOrgId, configType]);

  // Load this org's download presets for the "Default FTP preset" picker.
  useEffect(() => {
    if (!showPresetField || !firebase || !currentOrgId) return;
    firebase.getDownloadPresets(currentOrgId)
      .then((list: DownloadPreset[]) => setPresets(list || []))
      .catch((err: any) => console.error(`${logPrefix} failed to load presets:`, err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebase, currentOrgId, showPresetField]);

  // Load this org's distinct client names for the delivery client allowlist.
  useEffect(() => {
    if (!showClientFilter || !firebase || !currentOrgId) return;
    fetchOrgClients(firebase, currentOrgId)
      .then((cs: string[]) => setAvailableClients(cs || []))
      .catch((err: any) => console.error(`${logPrefix} failed to load clients:`, err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebase, currentOrgId, showClientFilter]);

  const handleSave = async () => {
    if (!firebase || !currentOrgId) return;
    setSaving(true);
    try {
      console.log(`${logPrefix} saving config for org:`, currentOrgId, { host: form.host, port: form.port, username: form.username, path: form.path, protocol: form.protocol });
      await firebase.callFunction('saveFtpConfig', { orgId: currentOrgId, configType, ...form });
      showSuccess('FTP configuration saved');
      setEditing(false);
      await loadStatus();
      onChanged?.();
    } catch (err: any) {
      console.error(`${logPrefix} save failed:`, err);
      showError(err.message || 'Failed to save FTP config');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    if (!firebase || !currentOrgId) return;
    setTesting(true);
    try {
      const res = await firebase.callFunction('testFtpConnection', { orgId: currentOrgId, configType });
      const data = res.data;
      console.log(`${logPrefix} test result:`, data);
      if (data.success) {
        showSuccess('FTP connection successful');
      } else {
        showError(data.error || 'FTP test failed');
      }
    } catch (err: any) {
      console.error(`${logPrefix} test failed:`, err);
      showError(err.message || 'FTP test failed');
    }
    setTesting(false);
  };

  const handleDisable = async () => {
    if (!firebase || !currentOrgId) return;
    const confirmMsg = configType === 'delivery'
      ? 'Disable Delivery FTP? Delivered assets will no longer be pushed to your external FTP.'
      : 'Disable Download FTP? "Send to FTP" will no longer be available in the Download Manager.';
    if (!window.confirm(confirmMsg)) return;
    try {
      await firebase.callFunction('disableFtpConfig', { orgId: currentOrgId, configType });
      showSuccess('FTP disabled');
      await loadStatus();
      onChanged?.();
    } catch (err: any) {
      console.error(`${logPrefix} disable failed:`, err);
      showError(err.message || 'Failed to disable FTP');
    }
  };

  if (loading) return (
    <div className="mt-3" style={{ color: '#fff' }}>
      <Spinner size="sm" style={{ color: '#fff' }} /> Loading {title}…
    </div>
  );

  return (
    <div className="mt-4">
      <h6 className="mb-1" style={{ color: '#fff' }}>{title}</h6>
      <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>{description}</p>

      {!editing && status.configured ? (
        <div style={{ padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ color: '#fff', fontSize: '0.9rem' }}>
              <strong>{status.protocol?.toUpperCase()}</strong> {status.username}@{status.host}:{status.port}{status.path}
              {status.active === false && (
                <span style={{ marginLeft: 10, fontSize: '0.75rem', color: 'var(--button-danger)' }}>(disabled)</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button className="btn-primary-custom btn-round btn-sm" onClick={handleTest} disabled={testing}>
                <span>{testing ? 'Testing…' : 'Test'}</span>
              </Button>
              <Button className="btn-primary-custom btn-round btn-sm" onClick={() => setEditing(true)}>
                <span>Edit</span>
              </Button>
              {status.active !== false && (
                <Button className="btn-danger-custom btn-round btn-sm" onClick={handleDisable}>
                  <span>Disable</span>
                </Button>
              )}
            </div>
          </div>
          {showClientFilter && (
            <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' }}>
              Clients:&nbsp;
              {(!status.clientFilter || status.clientFilter.length === 0)
                ? <span style={{ color: 'rgba(255,255,255,0.75)' }}>All clients</span>
                : <span style={{ color: 'rgba(255,255,255,0.75)' }}>{status.clientFilter.join(', ')}</span>}
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 16px', alignItems: 'center', fontSize: '0.875rem' }}>
            <label className="text-muted mb-0">Protocol</label>
            <select
              className="form-control"
              style={{ maxWidth: 260 }}
              value={form.protocol}
              onChange={(e) => {
                const nextProtocol = e.target.value;
                // Snap port to the protocol default when toggling, but only if the
                // user still has the old default — preserves custom ports.
                const nextPort = form.port === 21 || form.port === 22
                  ? (nextProtocol === 'ftp' ? 21 : 22)
                  : form.port;
                setForm({ ...form, protocol: nextProtocol, port: nextPort });
              }}
            >
              <option value="sftp">SFTP (recommended, port 22)</option>
              <option value="ftp">FTP (legacy, passive mode, port 21)</option>
            </select>

            <label className="text-muted mb-0">Host</label>
            <input
              type="text"
              className="form-control"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              placeholder="sftp.example.com"
            />

            <label className="text-muted mb-0">Port</label>
            <input
              type="number"
              className="form-control"
              style={{ maxWidth: 120 }}
              value={form.port}
              onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 22 })}
            />

            <label className="text-muted mb-0">Username</label>
            <input
              type="text"
              className="form-control"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />

            <label className="text-muted mb-0">Password</label>
            <input
              type="password"
              className="form-control"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={status.configured ? '(unchanged if blank)' : ''}
            />

            <label className="text-muted mb-0">Path</label>
            <input
              type="text"
              className="form-control"
              value={form.path}
              onChange={(e) => setForm({ ...form, path: e.target.value })}
              placeholder="/incoming/spots/"
            />

            {showPresetField && (
              <>
                <label className="text-muted mb-0">Default FTP preset</label>
                <select
                  className="form-control"
                  value={form.defaultFtpPresetId}
                  onChange={(e) => setForm({ ...form, defaultFtpPresetId: e.target.value })}
                >
                  <option value="">None — deliver files as-is</option>
                  {presets.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.format && p.format !== 'original' ? ` · ${p.format}` : ''}
                      {p.prefix || p.suffix ? ` · ${p.prefix || ''}…${p.suffix || ''}` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}

            {showClientFilter && (
              <>
                <label className="text-muted mb-0" style={{ alignSelf: 'start', paddingTop: 6 }}>Clients</label>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem', marginBottom: 6 }}>
                    Only deliver campaigns for the selected clients. Leave all unchecked to deliver every client's assets.
                  </div>
                  {availableClients.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                      No clients found yet — they appear here once campaigns exist.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                        <button type="button" onClick={() => setForm({ ...form, clientFilter: [...availableClients] })}
                          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent-1-color)', cursor: 'pointer', fontSize: '0.75rem' }}>
                          Select all
                        </button>
                        <button type="button" onClick={() => setForm({ ...form, clientFilter: [] })}
                          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent-1-color)', cursor: 'pointer', fontSize: '0.75rem' }}>
                          Clear
                        </button>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                          {form.clientFilter.length === 0 ? 'All clients' : `${form.clientFilter.length} selected`}
                        </span>
                      </div>
                      <div style={{ maxHeight: 170, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 10px', backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 4 }}>
                        {availableClients.map((c) => {
                          const checked = form.clientFilter.includes(c);
                          return (
                            <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontSize: '0.8rem', cursor: 'pointer', margin: 0 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setForm({
                                  ...form,
                                  clientFilter: checked
                                    ? form.clientFilter.filter((x) => x !== c)
                                    : [...form.clientFilter, c],
                                })}
                              />
                              {c}
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="mt-3" style={{ display: 'flex', gap: 8 }}>
            <Button className="btn-primary-custom btn-round btn-sm" onClick={handleSave} disabled={saving || !form.host || !form.username}>
              <span>{saving ? 'Saving…' : 'Save'}</span>
            </Button>
            {status.configured && (
              <Button className="btn-info-custom btn-round btn-sm" onClick={() => setEditing(false)} disabled={saving}>
                <span>Cancel</span>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FtpConfigPanel;
